// ============================================================================
// useCan — Canonical permission-check hook for screens (Tenant Hub edition)
// ============================================================================
//   const can = useCan('ENTITY_MASTER');   // catalog screen code
//   if (can('CREATE')) { ... }
//   { ...actionButtonFactory.add(handleAdd), visible: can('CREATE') }
//
// Ported from react-project-template 1.2.2 with one Tenant Hub rule added:
// the platform administrator (enterprise-admin login) has no permission
// context — the Hub is identity-only and the admin's authority comes from
// the Keycloak role, enforced by the API on every call — so for that login
// every check returns true. For a tenant-user session the check resolves
// against the permission engine exactly as in the template.
//
// REACT_APP_IS_DEV_DEMO=true bypasses every check during scaffold bring-up.
// Leave it unset outside local development.

import { useMemo } from 'react';
import { useAuth, usePermissions } from 'hooks/usePermissions';

// Direct member access is required: CRA's DefinePlugin replaces the literal
// `process.env.REACT_APP_IS_DEV_DEMO` at compile time and does not recognise
// the optional-chained form.
// eslint-disable-next-line no-undef
const IS_DEV_DEMO = process.env.REACT_APP_IS_DEV_DEMO === 'true';

/** Roles whose authority the API enforces itself; the UI gates nothing for them. */
const PLATFORM_ADMIN_ROLES = ['enterprise_admin', 'super_admin'];

export type CanFn = (action: string) => boolean;

/**
 * Returns a memoised `(action) => boolean` checker bound to one resource.
 *
 * `resource` is the catalog screen code (UPPER_SNAKE), never the sidebar nav id.
 * `action` is the UPPER_SNAKE_CASE verb (CREATE / UPDATE / DELETE / EXPORT / …).
 */
export function useCan(resource: string): CanFn {
  const permissions = usePermissions();
  const { user } = useAuth();
  const isPlatformAdmin = PLATFORM_ADMIN_ROLES.includes((user?.userRole || '').toLowerCase());
  return useMemo(
    () => (action: string) => IS_DEV_DEMO || isPlatformAdmin || permissions.hasAction(resource, action),
    [permissions, resource, isPlatformAdmin],
  );
}

/** Exposed for callers that need to know whether dev-demo mode is on. */
export const isDevDemoBypass = IS_DEV_DEMO;
